/**
 * Waits for variable to exist
 * @param  {object}   name   Variable that you're waiting for
 * @param  {function} callback  Function that runs after variable has been found
 */
const whenReady = (name, callback) => {
    var interval = 10; // ms
    window.setTimeout(function() {
        if (window[name]) {
            callback(window[name]);
        } else {
            whenReady(name, callback);
        }
    }, interval);
}

// WAIT FOR ELEMENT TO LOAD ON TO PAGE
function waitForElm(selector) {
	return new Promise((resolve) => {
		if (document.querySelector(selector)) {
			return resolve(document.querySelector(selector));
		}

		const observer = new MutationObserver((mutations) => {
			if (document.querySelector(selector)) {
				resolve(document.querySelector(selector));
				observer.disconnect();
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	});
}

// Map to hold active focus traps and previous focus state
const traps = new Map();

/**
 * Check if an element is visible considering its ancestors.
 * @param {HTMLElement} element - The element to check.
 * @returns {boolean} - True if the element is visible, false otherwise.
 */
const isFocusElVisible = (element) => {
    // If the element itself is not in the document, return false
    if (!element || element.nodeType !== Node.ELEMENT_NODE) {
        return false;
    }

    // Check the visibility of the element itself
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
        return false;
    }

    // Check parent elements recursively
    const parent = element.parentElement;
    if (parent) {
        return isFocusElVisible(parent); // Recursively check the parent
    }

    return true; // Reached the topmost parent without finding hidden elements
};

/**
 * Trap focus within a given container element.
 * @param {HTMLElement} container - The element within which to trap focus.
 * @example trap(document.getElementById('myModal'));
 * // Call untrap(myModal) to disable focus trapping when done.
 */
const trap = (container, closeCallback) => {
    // If there's already a trap for the container, do nothing
    if (traps.has(container)) {
        return;
    }

    // Select all focusable elements within the container and filter out hidden ones
    const focusableElements = Array.from(container.querySelectorAll(
        'a[href]:not([tabindex="-1"]), ' + // Links
        'area[href]:not([tabindex="-1"]), ' + // Area links
        'input:not([disabled]):not([tabindex="-1"]), ' + // Input fields
        'select:not([disabled]):not([tabindex="-1"]), ' + // Select dropdowns
        'textarea:not([disabled]):not([tabindex="-1"]), ' + // Text area
        'button:not([disabled]):not([tabindex="-1"]), ' + // Buttons
        'iframe, ' + // Iframes
        'video[controls], ' + // Video with controls
        'audio[controls], ' + // Audio with controls
        'summary, ' + // Summary element for details
        '[contenteditable]:not([tabindex="-1"]), ' + // ContentEditable elements
        '*[tabindex]:not([tabindex="-1"]), ' + // Elements with tabindex
        'details[open], ' + // Open details element
        'time[datetime], ' + // Time element can be focusable in some contexts
        'dialog' // Dialog elements (HTML5)
    )).filter(isFocusElVisible);    

    // Handle case if there are no focusable elements
    if (focusableElements.length === 0) {
        console.warn("No focusable elements found in the trap container.");
        return;
    }

    // console.log(focusableElements)

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const previousActiveElement = document.activeElement;

    // Function to handle keydown event for trapping focus
    const handleKeydown = (event) => {
        // Check if the Tab key is pressed
        if (event.key === 'Tab') {
            // If Shift + Tab, move focus backwards
            if (event.shiftKey) {
                // If the first element is focused, focus the last element
                if (document.activeElement === firstElement) {
                    event.preventDefault(); // Prevent default focus action
                    lastElement.focus(); // Focus the last element
                }
            } else { // If just Tab, move focus forwards
                // If the last element is focused, focus the first element
                if (document.activeElement === lastElement) {
                    event.preventDefault(); // Prevent default focus action
                    firstElement.focus(); // Focus the first element
                }
            }
        }

        // Allow closing modal with Escape key
        if (event.key === 'Escape' && closeCallback !== undefined) {
            // Implement close logic here or call untrap
            untrap(container); // Call untrap to release focus trapping
            closeCallback();
        }
        
    };

    // Add event listener for keydown to the container
    container.addEventListener('keydown', handleKeydown);

    // Focus the first element when the modal is opened
    const focusOnOpen = () => {
        firstElement.focus(); // Focus on the first focusable element
    };

    focusOnOpen(); // Call immediately to ensure focus on open

    // Store the event listener and container in the traps object
    traps.set(container, {
        handleKeydown,
        previousActiveElement
    });
};

/**
 * Untrap focus in the given container element.
 * @param {HTMLElement} container - The element from which to remove focus trapping.
 * @example untrap(document.getElementById('myModal'));
 */
const untrap = (container) => {
    // Get the associated keydown handler for the container
    const trapData = traps.get(container);
    if (trapData) {
        container.removeEventListener('keydown', trapData.handleKeydown); // Remove the listener
        traps.delete(container); // Remove the entry from the traps map

        // Restore focus to the previously active element when possible
        const shouldRestoreFocus = trapData.previousActiveElement
            && trapData.previousActiveElement !== document.body
            && document.contains(trapData.previousActiveElement)
            && isFocusElVisible(trapData.previousActiveElement)
            && !trapData.previousActiveElement.hasAttribute('disabled');

        if (shouldRestoreFocus) {
            trapData.previousActiveElement.focus();
        }
    }
};


window.trapFocus = {};
window.trapFocus.trap = trap;
window.trapFocus.untrap = untrap;